import React, { useState, useCallback } from 'react';
import { AlertContext } from '../contexs/alert-context';
import {
  ModalConfirmation,
  ModalConfirmationProp,
} from '../components/shared/modals/modal-confirmation';

export const AlertProvider = ({ children }: { children: React.ReactNode }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [content, setContent] = useState('');
  const [modalOptions, setModalOptions] = useState<
    Partial<ModalConfirmationProp>
  >({});
  // Used for confirm implementation
  const [resolvePromise, setResolvePromise] = useState<
    ((confirmed: boolean) => void) | null
  >(null);

  const show = (message: string) => {
    setContent(message);
    setIsVisible(true);
  };

  const hide = () => {
    setContent('');
    setIsVisible(false);
  };

  // New confirm method that returns a promise
  const confirm = useCallback(
    (message: string, options: Partial<ModalConfirmationProp> = {}) => {
      show(message);
      setModalOptions({ hideCancel: false, ...options });
      setIsVisible(true);

      return new Promise<boolean>(resolve => {
        setResolvePromise(() => resolve);
      });
    },
    [],
  );

  // Handler for when the user confirms in the modal.
  const onConfirm = () => {
    if (modalOptions.onConfirm) {
      modalOptions.onConfirm();
    }
    if (resolvePromise) {
      resolvePromise(true);
      setResolvePromise(null);
    }
    hide();
  };

  // Handler for when the user cancels (or the modal is hidden).
  const onCancel = () => {
    if (modalOptions.onModalHide) {
      modalOptions.onModalHide();
    }
    if (resolvePromise) {
      resolvePromise(false);
      setResolvePromise(null);
    }
    hide();
  };

  return (
    <AlertContext.Provider
      value={{ isVisible, show, hide, setModalOptions, confirm }}>
      {children}

      {isVisible && (
        <ModalConfirmation
          {...modalOptions}
          isVisible={isVisible}
          onModalHide={onCancel}
          hideCancel={modalOptions.hideCancel ?? true}
          content={content}
          onConfirm={onConfirm}>
          {modalOptions.children ? modalOptions.children : null}
        </ModalConfirmation>
      )}
    </AlertContext.Provider>
  );
};
